import { Entity, Location } from '@nx-smartmonkey/shared/domain';
import { STOP_TASK_STATUS, StopStatus } from '../../constants/Stops';
import { StopReport } from './StopReport';
import { StopTask } from './StopTask';

export const LARGE_OPTIMIZATION_MAX_SERVICES = 1000;

export type MimeTypes = `image/jpeg` | `image/jpg` | `image/png` | `image/webp`;

export interface ImageUploadedProps {
  tempId: string;
  file_name: string;
  mime_type: MimeTypes;
  data: Blob;
}
export interface RouteStopFieldProps {
  id: string;
  type: ProjectDriversFieldTypes;
  value: any;
  label?: string;
  is_custom_field: boolean;
}
export type ProjectDriversFieldTypes =
  | `string`
  | `number`
  | `time`
  | `time_window`
  | `phone`
  | `url`
  | `multiselect`
  | `select`
  | `boolean`
  | `email`;

export type StopType = `pickup` | `delivery`;

export interface RouteStopReportDTO {
  images: boolean;
  signature: boolean;
}

export interface StopProps {
  route_id?: string;
  label?: string;
  location?: Location;
  type: StopType;
  pickup_id?: string;
  order: number;
  tasks: StopTask[];
  stop_fields: RouteStopFieldProps[];
  status: StopStatus;
  customer_text_field?: string;
  report?: StopReport;
  optimization_warning?: boolean;

  // Custom stop fields
  isSpecificStop?: boolean;
}

export class Stop extends Entity<StopProps> {
  get customer_text_field(): string | undefined {
    return this.props.customer_text_field;
  }
  get stop_fields(): RouteStopFieldProps[] {
    return this.props.stop_fields;
  }

  getStopFieldById(id: string): RouteStopFieldProps | undefined {
    return this.props.stop_fields.find((field) => !field.is_custom_field && field.id === id);
  }

  updateStopFieldById(id: string, value: any): void {
    this.props.stop_fields.forEach((field) => {
      if (!field.is_custom_field && field.id === id) {
        field.value = value;
      }
    });
  }

  getStopFieldCustomFieldById(id: string): RouteStopFieldProps | undefined {
    return this.props.stop_fields.find((field) => field.is_custom_field && field.id === id);
  }

  updateStopFieldCustomFieldById(id: string, value: string | number[] | number | boolean): void {
    this.props.stop_fields.forEach((field) => {
      if (field.is_custom_field && field.id === id) {
        field.value = value;
      }
    });
  }

  updateByStop(stop: Stop): void {
    this.props.label = stop.label;
    this.props.stop_fields.map((stopField) => {
      stopField.value = stop.stop_fields.find((sf) => sf.id === stopField.id)?.value;
      return stopField;
    });
  }

  get id(): string {
    return this._id!;
  }

  get label(): string | undefined {
    return this.props.label;
  }

  set label(newLabel: string | undefined) {
    this.props.label = newLabel;
  }

  get location(): Location | undefined {
    return this.props.location;
  }

  set location(location: Location | undefined) {
    this.props.location = location;
  }

  get type(): StopType {
    return this.props.type;
  }

  get route_id(): string | undefined {
    return this.props.route_id;
  }

  get pickup_id(): string | undefined {
    return this.props.pickup_id;
  }

  get order(): number {
    return this.props.order;
  }
  set order(value: number) {
    this.props.order = value;
  }

  get status(): StopStatus {
    return this.props.status;
  }

  set status(value: StopStatus) {
    this.props.status = value;
  }

  get report(): StopReport | undefined {
    return this.props.report;
  }

  set report(report: StopReport | undefined) {
    this.props.report = report;
  }

  get optimization_warning(): boolean {
    return (this.props.status === `pending` && this.props.optimization_warning) ?? false;
  }

  get tasks(): StopTask[] {
    return this.props.tasks;
  }

  get isSpecificStop(): boolean {
    return this.props.isSpecificStop || false;
  }

  set isSpecificStop(value: boolean) {
    this.props.isSpecificStop = value;
  }

  getPendingTasks(): StopTask[] {
    return this.props.tasks.filter((task) => task.status === STOP_TASK_STATUS.pending);
  }

  private constructor(props: StopProps, id: string) {
    super(props, id);
  }

  static create(props: StopProps, id: string): Stop {
    return new Stop(props, id);
  }
}
